export interface NavChild {
  label: string;
  href: string;
  disabled?: boolean;
  /** Hidden unless the current user has this permission. */
  permission?: string;
  badge?: string;
}

export interface NavItem {
  label: string;
  href?: string;
  icon?: string;
  children?: NavChild[];
  permission?: string;
  badge?: string;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * Systematic Enterprise Navigation structure for MMS ERP.
 * Logically organized into functional business workflows with clear, intuitive labels.
 */
export const navigationSections: NavSection[] = [
  {
    title: 'OVERVIEW & ANALYTICS',
    items: [
      {
        label: 'Dashboard',
        href: '/',
        icon: 'dashboard',
      },
      {
        label: 'Alerts & Activity',
        href: '/alerts',
        icon: 'alert',
      },
      {
        label: 'Reports & Analytics',
        href: '/reports',
        icon: 'chart',
      },
    ],
  },
  {
    title: 'SALES & MARKETPLACES',
    items: [
      {
        label: 'Marketplace Channels',
        icon: 'marketplace',
        children: [
          { label: 'Marketplaces Hub', href: '/marketplaces' },
          { label: 'Flipkart Operations Hub', href: '/marketplaces/flipkart' },
          { label: 'Flipkart Packing Station', href: '/marketplaces/flipkart/packing', permission: 'marketplaces.pack' },
          { label: 'Amazon Seller Hub', href: '/marketplaces/amazon', permission: 'marketplaces.view' },
          { label: 'Connected eCommerce Stores', href: '/marketplaces/websites' },
          { label: 'Multi-Channel Listings', href: '/listings' },
        ],
      },
      {
        label: 'Orders & Reverse Logistics',
        icon: 'orders',
        children: [
          { label: 'All Customer Orders', href: '/orders' },
          { label: 'Shipments & Tracking', href: '/shipments' },
          { label: 'Returns & Reverse QC', href: '/returns' },
          { label: 'Refunds & Dispute Tracking', href: '/refunds' },
        ],
      },
    ],
  },
  {
    title: 'CATALOG & MERCHANDISING',
    items: [
      {
        label: 'Catalog & Products',
        icon: 'catalog',
        permission: 'products.view',
        children: [
          { label: 'Products Directory', href: '/products', permission: 'products.view' },
          { label: 'SKU Directory & Variants', href: '/skus', permission: 'skus.view' },
          { label: 'Product Bundles / Combos', href: '/bundles', permission: 'bundles.view' },
          { label: 'Categories', href: '/categories', permission: 'categories.view' },
          { label: 'Brand Registry', href: '/brands', permission: 'brands.view' },
        ],
      },
      {
        label: 'Procurement & Purchasing',
        icon: 'purchasing',
        permission: 'purchases.view',
        children: [
          { label: 'Purchase Orders', href: '/purchases', permission: 'purchases.view' },
          { label: 'Goods Receipts (GRN)', href: '/goods-receipts', permission: 'goods_receipts.view' },
          { label: 'Suppliers & Vendors', href: '/suppliers', permission: 'suppliers.view' },
        ],
      },
    ],
  },
  {
    title: 'INVENTORY & WAREHOUSING',
    items: [
      {
        label: 'Inventory Management',
        icon: 'inventory',
        permission: 'inventory.view',
        children: [
          { label: 'Stock Overview & Balances', href: '/inventory' },
          { label: 'Central IMS Transfers', href: '/inventory/ims-transfers' },
          { label: 'Internal Warehouse Transfers', href: '/inventory/transfers', permission: 'inventory.transfer' },
          { label: 'Stock Adjustments & Safety', href: '/inventory/adjustments', permission: 'inventory.adjust' },
          { label: 'Ledger & Stock Movements', href: '/inventory/movements' },
          { label: 'Sales Channel Pools', href: '/inventory/pools' },
          { label: 'Warehouses & Locations', href: '/warehouses', permission: 'warehouses.view' },
        ],
      },
    ],
  },
  {
    title: 'FINANCE & PROFITABILITY',
    items: [
      {
        label: 'Finance & Accounts',
        icon: 'finance',
        permission: 'profitability.view',
        children: [
          { label: 'Settlements & Payouts', href: '/settlements' },
          { label: 'Order Profitability (P&L)', href: '/profit-loss', permission: 'profitability.view' },
          { label: 'Operating Expenses', href: '/expenses', permission: 'profitability.view' },
          { label: 'Financial Periods', href: '/profit-loss/periods', permission: 'profitability.period_view' },
          { label: 'Prior P&L Adjustments', href: '/profit-loss/adjustments', permission: 'profitability.period_view' },
        ],
      },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      {
        label: 'System Administration',
        icon: 'admin',
        children: [
          { label: 'Companies & Entities', href: '/companies', permission: 'companies.view' },
          { label: 'User Roles & Access', href: '/users', permission: 'users.view' },
          { label: 'System Settings', href: '/settings' },
        ],
      },
    ],
  },
];

// Flat export for compatibility
export const navigation: NavItem[] = navigationSections.flatMap((s) => s.items);
